import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useResourceList, useResourceMutations } from "@/hooks/use-admin-resource";
import { AdminPageHeader, RowActions, ResponsiveTableWrap } from "@/components/admin/crud-helpers";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, ArrowDown } from "lucide-react";
import { ImageUploadField } from "@/components/admin/image-upload-field";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/admin/team")({
  component: TeamAdmin,
});

type TeamMember = {
  id: string;
  name: string;
  role: string;
  photo_url: string | null;
  bio: string | null;
  location: string | null;
  department: string | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  github_url: string | null;
  email: string | null;
  sort_order: number;
  published: boolean;
};

function TeamAdmin() {
  const { data, isLoading } = useResourceList<TeamMember>("team_members", {
    orderBy: "sort_order",
    ascending: true,
  });
  const { create, update, remove } = useResourceMutations("team_members");
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState<string>("all");
  const [statusF, setStatusF] = useState<"all" | "live" | "hidden">("all");

  const depts = useMemo(() => {
    const s = new Set<string>();
    (data ?? []).forEach((m) => m.department && s.add(m.department));
    return Array.from(s).sort();
  }, [data]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data ?? []).filter((m) => {
      if (dept !== "all" && m.department !== dept) return false;
      if (statusF === "live" && !m.published) return false;
      if (statusF === "hidden" && m.published) return false;
      if (!s) return true;
      return (
        m.name.toLowerCase().includes(s) ||
        (m.role ?? "").toLowerCase().includes(s) ||
        (m.department ?? "").toLowerCase().includes(s) ||
        (m.location ?? "").toLowerCase().includes(s)
      );
    });
  }, [data, q, dept, statusF]);

  const duplicate = (m: TeamMember) => {
    const { id, ...rest } = m;
    void id;
    create.mutate({ ...rest, name: `${m.name} (Copy)`, published: false, sort_order: (m.sort_order ?? 0) + 1 } as any);
  };

  const move = (m: TeamMember, dir: -1 | 1) => {
    update.mutate({ id: m.id, values: { sort_order: (m.sort_order ?? 0) + dir } });
  };

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Team members"
        description="People shown on the public About page."
        search={q}
        onSearchChange={setQ}
        searchPlaceholder="Search name, role, department…"
        count={data?.length}
        countLabel="members"
        onNew={() => {
          setEditing(null);
          setOpen(true);
        }}
        newLabel="New member"
      />

      <div className="flex flex-wrap gap-1.5">
        {(["all", "live", "hidden"] as const).map((s) => (
          <button key={s} onClick={() => setStatusF(s)} className={`rounded-full border px-2.5 py-1 text-xs capitalize ${statusF === s ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary"}`}>{s}</button>
        ))}
        {depts.length > 0 && <span className="mx-1 text-xs text-muted-foreground self-center">·</span>}
        {depts.length > 0 && (
          <button onClick={() => setDept("all")} className={`rounded-full border px-2.5 py-1 text-xs ${dept === "all" ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary"}`}>All depts</button>
        )}
        {depts.map((d) => (
          <button key={d} onClick={() => setDept(d)} className={`rounded-full border px-2.5 py-1 text-xs ${dept === d ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/30 text-muted-foreground hover:bg-secondary"}`}>{d}</button>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card">
        <ResponsiveTableWrap>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Person</TableHead>
              <TableHead className="hidden sm:table-cell">Department</TableHead>
              <TableHead className="hidden md:table-cell">Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                  No team members match.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((m) => (
              <TableRow key={m.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {m.photo_url ? (
                      <img
                        src={m.photo_url}
                        alt={m.name}
                        className="h-9 w-9 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-secondary" />
                    )}
                    <div className="min-w-0">
                      <div className="truncate font-medium">{m.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[m.role, m.location, m.department].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                  {m.department ?? "—"}
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(m, -1)} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></Button>
                    <span className="w-6 text-center">{m.sort_order}</span>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(m, 1)} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={m.published ? "default" : "secondary"}>
                    {m.published ? "Live" : "Hidden"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <RowActions
                    onEdit={() => {
                      setEditing(m);
                      setOpen(true);
                    }}
                    onDuplicate={() => duplicate(m)}
                    onDelete={() => {
                      if (confirm("Delete this team member?")) remove.mutate(m.id);
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </ResponsiveTableWrap>
      </div>

      <TeamMemberDialog
        key={editing?.id ?? "new"}
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        onSubmit={(v: any) => {
          if (editing)
            update.mutate({ id: editing.id, values: v }, { onSuccess: () => setOpen(false) });
          else create.mutate(v, { onSuccess: () => setOpen(false) });
        }}
      />
    </div>
  );
}

function TeamMemberDialog({ open, onOpenChange, editing, onSubmit }: any) {
  const [name, setName] = useState(editing?.name ?? "");
  const [role, setRole] = useState(editing?.role ?? "");
  const [photo, setPhoto] = useState(editing?.photo_url ?? "");
  const [bio, setBio] = useState(editing?.bio ?? "");
  const [location, setLocation] = useState(editing?.location ?? "");
  const [department, setDepartment] = useState(editing?.department ?? "");
  const [linkedin, setLinkedin] = useState(editing?.linkedin_url ?? "");
  const [twitter, setTwitter] = useState(editing?.twitter_url ?? "");
  const [github, setGithub] = useState(editing?.github_url ?? "");
  const [email, setEmail] = useState(editing?.email ?? "");
  const [sortOrder, setSortOrder] = useState<number>(editing?.sort_order ?? 0);
  const [published, setPublished] = useState(editing?.published ?? true);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit team member" : "New team member"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Role / title</Label>
              <Input value={role} onChange={(e) => setRole(e.target.value)} />
            </div>
          </div>
          <ImageUploadField
            label="Photo"
            value={photo || undefined}
            onChange={(url) => setPhoto(url ?? "")}
            folder="team"
          />
          <div className="grid gap-2">
            <Label>Short bio</Label>
            <Textarea rows={3} value={bio} onChange={(e) => setBio(e.target.value)} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Riyadh" />
            </div>
            <div className="grid gap-2">
              <Label>Department</Label>
              <Input
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="Engineering"
              />
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>LinkedIn URL</Label>
              <Input value={linkedin} onChange={(e) => setLinkedin(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>X / Twitter URL</Label>
              <Input value={twitter} onChange={(e) => setTwitter(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>GitHub URL</Label>
              <Input value={github} onChange={(e) => setGithub(e.target.value)} />
            </div>
            <div className="grid gap-2">
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>Sort order</Label>
              <Input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
              />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={published} onCheckedChange={setPublished} />
              <Label>Published</Label>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                name,
                role,
                photo_url: photo || null,
                bio: bio || null,
                location: location || null,
                department: department || null,
                linkedin_url: linkedin || null,
                twitter_url: twitter || null,
                github_url: github || null,
                email: email || null,
                sort_order: sortOrder,
                published,
              })
            }
          >
            {editing ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
